import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import styles from "./Select.module.scss";

type CategoryItem = {
	label: string;
	value: string;
	icon?: React.ReactNode;
};

type Props = {
	children?: React.ReactNode;
	label: string;
	placeholder: string;
	items: CategoryItem[];
	selected?: { label: string; value: string };
	required?: boolean;
	searchable?: boolean;
	error?: string;
	onChange?: (selected: { label: string; value: string } | undefined) => void;
};

const CategorySelect = memo((props: Props) => {
	const { children, label, onChange, searchable, placeholder, items, selected, required } = props;

	const [showOnTop, setShowOnTop] = useState(false);
	const [dropdownExpanded, setDropdownExpanded] = useState(false);
	const [selectedValue, setSelectedValue] = useState(selected);
	const [search, setSearch] = useState<string>("");
	const [value, setValue] = useState<string | null>(null);
	const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
	const inputRef = useRef<HTMLInputElement>(null);
	const itemsRef = useRef<HTMLDivElement>(null);

	const filteredItems = useMemo(() => {
		return items.filter(item => 
			item.label.toLowerCase().includes(search.toLowerCase())
		);
	}, [items, search]);

	const selectItem = useCallback(
		(label: string, value: string, event?: React.MouseEvent) => {
			const newSelection = { label, value };
			console.log('new select Item', newSelection);
			setSelectedValue(newSelection);
			setSearch("");
			setValue(label);
			setDropdownExpanded(false);
			if (onChange) {
				onChange(newSelection);
			}
		},
		[onChange]
	);

	function changeDropdownState(): void {
		setDropdownExpanded(!dropdownExpanded);
	}

	useEffect(() => {
		if (selected) setValue(selected.label);
	}, [selected]);

	const close = useCallback(() => {
		setDropdownExpanded(false);
		setSearch("");
		if (!value) {
			setValue("");
			if (onChange) {
				onChange(undefined);
			}
		}
	}, [value, onChange]);

	function handleMouseDown(event: React.MouseEvent): void {
		event.preventDefault();
	}

	function changeSearch(event: React.ChangeEvent<HTMLInputElement>): void {
		setSearch(event.target.value);
		setValue(event.target.value);
		setDropdownExpanded(true);
	}

	const handleKeyNavigation = useCallback(
		(event: KeyboardEvent) => {
			if (!dropdownExpanded) return;

			switch (event.key) {
				case "ArrowDown":
					event.preventDefault();
					setHighlightedIndex(prev => 
						prev < filteredItems.length - 1 ? prev + 1 : 0
					);
					break;
				case "ArrowUp":
					event.preventDefault();
					setHighlightedIndex(prev => 
						prev > 0 ? prev - 1 : filteredItems.length - 1
					);
					break;
				case "Enter":
					event.preventDefault();
					if (highlightedIndex >= 0 && filteredItems[highlightedIndex]) {
						const item = filteredItems[highlightedIndex];
						selectItem(item.label, item.value, event as any);
					}
					break;
				case "Escape":
					event.preventDefault();
					close();
					break;
			}
		},
		[highlightedIndex, filteredItems, dropdownExpanded, selectItem, close]
	);

	useEffect(() => {
		const inputElement = inputRef.current;
		if (inputElement) {
			inputElement.addEventListener("keydown", handleKeyNavigation);
			return () => {
				inputElement.removeEventListener("keydown", handleKeyNavigation);
			};
		}
	}, [handleKeyNavigation]);

	useEffect(() => {
		setSelectedValue(selected);
	}, [selected]);

	useEffect(() => {
		if (!dropdownExpanded) return;

		const dropdown = document.getElementById("select-button");
		if (!dropdown) return;

		const dropdownRect = dropdown.getBoundingClientRect();
		const bottomOverflow = window.innerHeight - (dropdownRect.bottom + window.innerHeight * 0.225);
		const topOverflow = dropdownRect.top - window.innerHeight * 0.225;

		setShowOnTop(bottomOverflow < 0 && topOverflow >= 0);
	}, [dropdownExpanded]);

	const handleContainerClick = () => {
		if (inputRef?.current) {
			inputRef.current.focus();
			changeDropdownState();
		}
	};

	return (
		<button type="button" className={`${styles.container} ${styles.categorySelectContainer}`} onClick={handleContainerClick}>
			{children && <div className={`${styles.inputIcon} ${styles.orangeBg}`}>{children}</div>}
			<div className={styles.inputContent}>
				<span className={styles.inputLabel}>
					{label} {required && <div className={styles.asterix}>&nbsp;*</div>}
				</span>
				<div role="none" className={styles.selectContainer} onBlur={close}>
					<div role="none" className={styles.selectButton} id="select-button" onClick={changeDropdownState} onBlur={close}>
						{searchable ? (
							<input 
								type="text" 
								ref={inputRef} 
								className={styles.searchInput} 
								placeholder={placeholder} 
								onChange={changeSearch} 
								value={value ?? search} 
							/>
						) : (
							<input
								readOnly
								ref={inputRef}
								className={`${styles.searchInput} ${styles.placeholderInput}`}
								placeholder={placeholder}
								value={selectedValue ? selectedValue.label : ""}
							/>
						)}
					</div>
					{props.error && <div className={styles.error}>{props.error}</div>}
					{
						<div
							id="select-dropdown"
							className={`${styles.selectExpanded} ${!dropdownExpanded ? styles.selectClosed : ""} ${showOnTop ? styles.selectExpandedTop : ""}`}
							role="none"
							onMouseDown={handleMouseDown}
						>
							<div className={`${styles.selectItems} ${styles.CategorySelectItem}`} ref={itemsRef}>
								{(() => {
									if (filteredItems.length === 0) {
										return <div className={styles.selectItem}>No items found</div>;
									}

									return filteredItems.map((item, index) => {
										const isSelected = selectedValue?.value === item.value;
										const isHighlighted = highlightedIndex === index;
										
										return (
											<button 
												onClick={() => selectItem(item.label, item.value)} 
												type="button" 
												key={item.value} 
												className={`${styles.categoryGroup} ${isHighlighted ? styles.highlighted : ''}`}
											>
												<div className={styles.categorySection}>
													{item.icon && (
														<div className={styles.itemIcon}>
															{item.icon}
														</div>
													)}
													<div className={styles.categoryLabel}>{item.label}</div>
												</div>
												{isSelected && (
													<div className={styles.checkmark}>
														<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path
																fillRule="evenodd"
																clipRule="evenodd"
																d="M13.7071 4.29289C14.0976 4.68342 14.0976 5.31658 13.7071 5.70711L7.70711 11.7071C7.31658 12.0976 6.68342 12.0976 6.29289 11.7071L3.29289 8.70711C2.90237 8.31658 2.90237 7.68342 3.29289 7.29289C3.68342 6.90237 4.31658 6.90237 4.70711 7.29289L7 9.58579L12.2929 4.29289C12.6834 3.90237 13.3166 3.90237 13.7071 4.29289Z"
																fill="#FF7926"
															/>
														</svg>
													</div>
												)}
											</button>
										);
									});
								})()}
							</div>
						</div>
					}
				</div>
			</div>
			<button type="button" className={`standardButton ${styles.selectBtn} ${styles.MultiSelectBtn}`} onClick={changeDropdownState}>
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M4.23447 5.83438C4.3845 5.6844 4.58794 5.60015 4.80007 5.60015C5.0122 5.60015 5.21565 5.6844 5.36567 5.83438L8.00007 8.46878L10.6345 5.83438C10.7083 5.75797 10.7965 5.69703 10.8941 5.6551C10.9918 5.61317 11.0967 5.5911 11.203 5.59018C11.3092 5.58926 11.4145 5.6095 11.5128 5.64972C11.6112 5.68995 11.7005 5.74935 11.7756 5.82446C11.8507 5.89958 11.9101 5.9889 11.9503 6.08722C11.9906 6.18553 12.0108 6.29088 12.0099 6.3971C12.009 6.50332 11.9869 6.6083 11.945 6.7059C11.903 6.80351 11.8421 6.89178 11.7657 6.96558L8.56567 10.1656C8.41565 10.3156 8.2122 10.3998 8.00007 10.3998C7.78794 10.3998 7.5845 10.3156 7.43447 10.1656L4.23447 6.96558C4.0845 6.81556 4.00024 6.61211 4.00024 6.39998C4.00024 6.18785 4.0845 5.9844 4.23447 5.83438Z"
						fill="#FF7926"
					/>
				</svg>
			</button>
		</button>
	);
});

export default CategorySelect;